import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
      isActive
        ? "text-primary-700 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-background-50/90 backdrop-blur-md border-b border-background-200">
      <div className="h-full px-4 md:px-6 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="一起草视频首页">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
            <i className="ri-play-circle-fill text-xl" />
          </span>
          <span className="text-lg font-bold text-foreground-950 tracking-tight whitespace-nowrap">
            一起草<span className="text-primary-600">视频</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2" aria-label="主导航">
          <NavLink to="/" end className={navItemClass}>
            首页
          </NavLink>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 bg-background-50 rounded-xl border border-background-200 p-2 animate-fade-in">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-background-100 transition-colors"
                  >
                    <span className="w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                      <i className={`${c.icon} text-base`} />
                    </span>
                    <span className="text-sm text-foreground-800">{c.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/list/film" className={navItemClass}>
            视频库
          </NavLink>
          <NavLink to="/about" className={navItemClass}>
            关于我们
          </NavLink>
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md ml-auto" role="search">
          <div className="relative w-full">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视、综艺、短视频…"
              className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100"
          aria-label="打开菜单"
          aria-expanded={mobileOpen}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="mb-4" role="search">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频…"
                className="w-full h-11 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-300"
              />
            </div>
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/" className="px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <Link to="/about" className="px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
          </div>
          <p className="mt-3 mb-2 px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wide">视频分类</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 text-sm text-foreground-800"
              >
                <span className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <i className={`${c.icon} text-sm`} />
                </span>
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}